export interface LnatSeoArticleSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface LnatSeoArticle {
  slug:
    | "what-is-lnat"
    | "how-to-prepare-6-weeks"
    | "section-a-strategy"
    | "section-b-essay-structure"
    | "common-mistakes"
    | "score-benchmarks-explained";
  title: string;
  description: string;
  summary: string;
  readTime: string;
  relatedSlugs: LnatSeoArticle["slug"][];
  sections: LnatSeoArticleSection[];
}

export const lnatSeoArticles: LnatSeoArticle[] = [
  {
    slug: "what-is-lnat",
    title: "What Is the LNAT? Structure, Timing, and Purpose",
    description:
      "A clear overview of the LNAT format, what Section A and Section B assess, and how universities typically use LNAT performance in admissions.",
    summary:
      "Understand what the LNAT tests, how it is structured, and what to focus on in your preparation.",
    readTime: "6 min read",
    relatedSlugs: ["section-a-strategy", "section-b-essay-structure"],
    sections: [
      {
        heading: "What the LNAT Actually Tests",
        paragraphs: [
          "The LNAT is an admissions assessment for law applicants. It is designed to test reasoning and communication under time pressure rather than prior legal knowledge.",
          "Strong performance usually depends on close reading, precise inference, and clear argument structure. It is less about memorising facts and more about disciplined thinking.",
        ],
      },
      {
        heading: "Section A and Section B",
        paragraphs: [
          "Section A is multiple-choice and focuses on comprehension and logical analysis. You are working with dense passages where wording matters.",
          "Section B is an essay that tests how well you can build and defend a position. Admissions teams often look for coherent structure, relevant reasoning, and balanced judgement.",
        ],
        bullets: [
          "Section A: reading precision, inference, consistency",
          "Section B: argument quality, organisation, clarity",
          "Both sections reward accuracy over speed-only tactics",
        ],
      },
      {
        heading: "How to Use This Information",
        paragraphs: [
          "Treat LNAT preparation as skills training. Focus on repeatable processes: reading method, option elimination, planning framework, and timed practice.",
          "Aim for steady improvement. Avoid promises about outcomes and concentrate on better decisions per question and stronger essay execution.",
        ],
      },
    ],
  },
  {
    slug: "how-to-prepare-6-weeks",
    title: "How to Prepare for the LNAT in 6 Weeks",
    description:
      "A practical six-week LNAT preparation plan covering Section A drills, Section B essay routines, and structured review without overtraining.",
    summary:
      "A realistic six-week preparation plan that balances Section A accuracy and Section B essay discipline.",
    readTime: "7 min read",
    relatedSlugs: ["section-a-strategy", "common-mistakes"],
    sections: [
      {
        heading: "Weeks 1-2: Build Core Technique",
        paragraphs: [
          "Start with untimed Section A sets to learn error patterns. Track why answers are wrong, not just whether they are wrong.",
          "For Section B, practise short plans before writing full essays. This helps you build structure first, then speed.",
        ],
      },
      {
        heading: "Weeks 3-4: Add Timed Constraints",
        paragraphs: [
          "Introduce timed mixed sets and keep a review log. If speed causes accuracy drops, slow down and correct process errors.",
          "Write full Section B responses on a schedule. Review introductions, paragraph logic, and conclusions for consistency.",
        ],
        bullets: [
          "Daily: focused Section A sets + review",
          "3-4 times weekly: timed essay drills",
          "Weekly: one full simulation block",
        ],
      },
      {
        heading: "Weeks 5-6: Simulation and Refinement",
        paragraphs: [
          "Run full-practice conditions and refine weak areas. Keep your routine stable in the final days rather than switching strategy late.",
          "Your target is reliable execution: clear reading process, controlled timing, and essays with defensible reasoning.",
        ],
      },
    ],
  },
  {
    slug: "section-a-strategy",
    title: "LNAT Section A Strategy: Accuracy Under Time Pressure",
    description:
      "Section A strategy for passage reading, option elimination, and time management to improve accuracy without rushing.",
    summary:
      "Improve Section A by tightening passage analysis, answer elimination, and timing discipline.",
    readTime: "6 min read",
    relatedSlugs: ["how-to-prepare-6-weeks", "common-mistakes"],
    sections: [
      {
        heading: "Read for Meaning, Not Speed Alone",
        paragraphs: [
          "Fast reading without comprehension creates costly errors. Focus on argument direction, qualifier words, and author stance.",
          "When a passage is dense, pause briefly to map its logic. A few seconds of structure can prevent multiple mistakes.",
        ],
      },
      {
        heading: "Use Elimination Intelligently",
        paragraphs: [
          "Most difficult items are solved by ruling out weak options. Eliminate answers that overstate, assume extra facts, or misread tone.",
          "When two choices look close, return to the exact line reference and test both options against wording precision.",
        ],
        bullets: [
          "Watch for absolute language that the passage does not support",
          "Reject options that add new claims not present in the text",
          "Prefer options that stay closest to the passage logic",
        ],
      },
      {
        heading: "Timing Protocol",
        paragraphs: [
          "Use checkpoint timing rather than question-by-question panic. If one item stalls, mark and move on.",
          "Consistent pacing plus disciplined review usually outperforms constant rushing.",
        ],
      },
    ],
  },
  {
    slug: "section-b-essay-structure",
    title: "LNAT Section B Essay Structure: A Clear Framework",
    description:
      "A practical Section B essay framework covering planning, argument balance, and concise conclusions for LNAT responses.",
    summary:
      "Use a simple essay framework to produce clear, balanced, and persuasive LNAT Section B responses.",
    readTime: "7 min read",
    relatedSlugs: ["what-is-lnat", "score-benchmarks-explained"],
    sections: [
      {
        heading: "Plan Before You Write",
        paragraphs: [
          "A short plan improves coherence and reduces repetition. Define your position, two supporting reasons, and one counterpoint response.",
          "Do not overplan. Keep it concise so most time is spent writing and refining.",
        ],
      },
      {
        heading: "Paragraph Logic",
        paragraphs: [
          "Each paragraph should have one core claim, supporting reasoning, and a clear link back to the question.",
          "Strong essays acknowledge complexity. Briefly engaging with opposing views often increases credibility.",
        ],
        bullets: [
          "Introduction: define scope and thesis",
          "Body: reasoned points with clear transitions",
          "Conclusion: judgment plus limits or implications",
        ],
      },
      {
        heading: "Style and Tone",
        paragraphs: [
          "Aim for precision over ornament. Avoid inflated language if it weakens clarity.",
          "Final minutes should be used for checking consistency, grammar, and whether the conclusion matches your argument.",
        ],
      },
    ],
  },
  {
    slug: "common-mistakes",
    title: "Common LNAT Mistakes and How to Avoid Them",
    description:
      "Common LNAT pitfalls in Section A and Section B, with practical corrections for timing, reasoning, and essay execution.",
    summary:
      "Avoid recurring LNAT errors by identifying predictable pitfalls and replacing them with repeatable routines.",
    readTime: "6 min read",
    relatedSlugs: ["section-a-strategy", "section-b-essay-structure"],
    sections: [
      {
        heading: "Mistake 1: Confusing Speed With Quality",
        paragraphs: [
          "Rushing without comprehension leads to avoidable errors. Good pacing should protect accuracy, not destroy it.",
          "Correction: adopt checkpoint timing and use elimination methodically.",
        ],
      },
      {
        heading: "Mistake 2: Weak Essay Structure",
        paragraphs: [
          "Unplanned essays often become descriptive rather than analytical. Admissions readers usually prefer structured reasoning over unsupported assertions.",
          "Correction: use a repeatable intro-body-conclusion framework and include a counterargument.",
        ],
      },
      {
        heading: "Mistake 3: Poor Review Discipline",
        paragraphs: [
          "Many candidates complete practice but never analyse why errors occurred. That limits improvement.",
          "Correction: keep a concise error log and revisit patterns weekly.",
        ],
        bullets: [
          "Track error type: misread, inference, tone, timing",
          "Record one fix per error pattern",
          "Retest old weaknesses under time pressure",
        ],
      },
    ],
  },
  {
    slug: "score-benchmarks-explained",
    title: "LNAT Score Benchmarks Explained (Without Hype)",
    description:
      "How to think about LNAT score benchmarks realistically, including context, variation by cycle, and why process quality still matters most.",
    summary:
      "Interpret LNAT benchmark ranges carefully and use them to guide preparation decisions, not unrealistic expectations.",
    readTime: "6 min read",
    relatedSlugs: ["what-is-lnat", "section-b-essay-structure"],
    sections: [
      {
        heading: "Why Benchmarks Need Context",
        paragraphs: [
          "Benchmark discussions can be useful, but they are not guarantees. Admissions decisions consider multiple application components.",
          "Ranges can shift by cycle and cohort. Treat benchmark data as directional rather than absolute.",
        ],
      },
      {
        heading: "Use Benchmarks as a Planning Tool",
        paragraphs: [
          "A benchmark can help you set weekly goals, but your day-to-day focus should remain on process quality: better reading decisions and clearer essays.",
          "If practice scores vary, prioritise consistency before chasing ambitious peaks.",
        ],
        bullets: [
          "Track trend over time, not a single result",
          "Pair score tracking with an error-pattern log",
          "Protect essay quality while improving Section A timing",
        ],
      },
      {
        heading: "Ethical Position",
        paragraphs: [
          "No responsible preparation provider can promise offers or outcomes. The strongest approach is disciplined preparation with honest self-review.",
          "Use benchmarks to calibrate effort, not to generate false certainty.",
        ],
      },
    ],
  },
];

export function getLnatSeoArticleBySlug(slug: string): LnatSeoArticle | null {
  return lnatSeoArticles.find((article) => article.slug === slug) ?? null;
}
